import React, { useEffect, useRef, useState } from "react";
import * as echarts from "echarts";
import { styled } from "@mui/material/styles";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    Box,
    CircularProgress,
    Alert,
} from "@mui/material";
import { getAverageWaitingTime } from "../../api/api";
import { getCurrentUser } from "../../utils/auth";

// Styled Table Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    fontWeight: "bold",
    textAlign: "center",
    backgroundColor: theme.palette.action.hover,
}));

const StyledTableRow = styled(TableRow)(() => ({
    "&:nth-of-type(odd)": {
        backgroundColor: "#f9f9f9",
    },
}));

const Customer_Minutes_Report = ({ startDate, endDate }) => {
    const chartRef = useRef(null);
    const [data, setData] = useState(null);
    const [overallStats, setOverallStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const user = getCurrentUser();
                setCurrentUser(user);

                const startDateParam = startDate ? startDate.format("YYYY-MM-DD") : undefined;
                const endDateParam = endDate ? endDate.format("YYYY-MM-DD") : undefined;
                const response = await getAverageWaitingTime(startDateParam, endDateParam);
                if (response.success) {
                    setData(response.data);
                    setOverallStats(response.overall_stats || null);
                } else {
                    setError(response.message || "Failed to fetch data");
                }
            } catch (err) {
                console.error("Error fetching waiting time data:", err);
                setError("Failed to load waiting time data");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [startDate, endDate]);

    useEffect(() => {
        if (!data || !chartRef.current) return;

        const chartInstance = echarts.init(chartRef.current);

        const days = data.map((item) => item.day);
        const avgWaitTimes = data.map((item) => item.avg_wait_time_minutes);

        const options = {
            title: {
                text: `Average Waiting Times by Day of Week${
                    currentUser?.branch_name
                        ? ` - ${currentUser.branch_name}`
                        : ""
                }`,
                left: "center",
                textStyle: { fontSize: 18, fontWeight: "bold" },
            },
            tooltip: {
                trigger: "axis",
                formatter: function (params) {
                    const data = params[0];
                    return `${data.name}<br/>Average Wait: ${data.value} minutes`;
                },
            },
            xAxis: {
                type: "category",
                data: days,
                axisLabel: { rotate: 30 },
            },
            yAxis: {
                type: "value",
                name: "Minutes",
            },
            series: [
                {
                    name: "Average Wait Time",
                    type: "bar",
                    data: avgWaitTimes,
                    itemStyle: {
                        color: "#4caf50",
                        borderColor: "#388e3c",
                        borderWidth: 2,
                    },
                    label: {
                        show: true,
                        position: "top",
                        formatter: "{c} min",
                    },
                },
            ],
        };

        chartInstance.setOption(options);

        const handleResize = () => chartInstance.resize();
        window.addEventListener("resize", handleResize);

        return () => {
            chartInstance.dispose();
            window.removeEventListener("resize", handleResize);
        };
    }, [data]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Summary Stats */}
            {data && data.length > 0 && (
                <Box
                    sx={{
                        mb: 3,
                        p: 2,
                        bgcolor: "background.paper",
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Summary Statistics
                        {currentUser?.branch_name
                            ? ` - ${currentUser.branch_name}`
                            : ""}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Average Wait Time
                            </Typography>
                            <Typography variant="h6">
                                {Math.round(
                                    data.reduce(
                                        (sum, item) =>
                                            sum + item.avg_wait_time_minutes,
                                        0
                                    ) / data.length
                                )}{" "}
                                min
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Longest Wait
                            </Typography>
                            <Typography variant="h6">
                                {Math.round(
                                    Math.max(
                                        ...data.map(
                                            (item) => item.max_wait_time_minutes
                                        )
                                    )
                                )}{" "}
                                min
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Shortest Wait
                            </Typography>
                            <Typography variant="h6">
                                {Math.round(
                                    Math.min(
                                        ...data.map(
                                            (item) => item.min_wait_time_minutes
                                        )
                                    )
                                )}{" "}
                                min
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                Total Customers
                            </Typography>
                            <Typography variant="h6">
                                {overallStats?.total_customers !== undefined
                                    ? overallStats.total_customers
                                    : data.reduce(
                                          (sum, item) => sum + (Number(item.total_customers) || 0),
                                          0
                                      )}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            )}

            {/* Table Section */}
            <Typography
                variant="h5"
                align="center"
                sx={{ fontWeight: "bold", mb: 2 }}
            >
                Average Waiting Times by Day of Week
                {currentUser?.branch_name
                    ? ` - ${currentUser.branch_name}`
                    : ""}
            </Typography>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table sx={{ minWidth: 600 }} aria-label="waiting times table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Day</StyledTableCell>
                            <StyledTableCell>Avg Wait (min)</StyledTableCell>
                            <StyledTableCell>Min Wait (min)</StyledTableCell>
                            <StyledTableCell>Max Wait (min)</StyledTableCell>
                            <StyledTableCell>Customers</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.map((row, index) => (
                            <StyledTableRow key={row.day || index}>
                                <StyledTableCell>{row.day}</StyledTableCell>
                                <StyledTableCell>
                                    {row.avg_wait_time_minutes}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {row.min_wait_time_minutes}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {row.max_wait_time_minutes}
                                </StyledTableCell>
                                <StyledTableCell>
                                    {row.total_customers}
                                </StyledTableCell>
                            </StyledTableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Chart Section */}
            <Typography variant="h6" align="center" sx={{ mb: 2 }}>
                Waiting Time Trends by Day
            </Typography>
            <Box
                ref={chartRef}
                sx={{
                    width: "100%",
                    height: 400,
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    boxShadow: 2,
                    backgroundColor: "#fff",
                }}
            />
        </Box>
    );
};

export default Customer_Minutes_Report;
